/**
 * Registers the offline / high-latency service worker.
 *
 * Called from main.ts after bootstrap so a failure can't block the
 * Angular app from starting. Skipped in environments where service
 * workers aren't available (SSR, older browsers, http://) and skipped
 * on localhost dev runs unless explicitly opted in via
 * `?sw=1` query string, so Angular's HMR isn't fighting cached assets.
 */
export function registerOfflineServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
  const optIn = new URLSearchParams(window.location.search).has('sw');
  if (isLocal && !optIn) {
    // A service worker registered earlier on this origin (e.g. from a one-off
    // ?sw=1 opt-in) keeps intercepting every later `npm start` run and serves
    // stale, cache-first chunks — white-screening lazy routes after a rebuild
    // and slowing navigation. Skipping registration isn't enough once one
    // exists, so actively tear it down and evict its caches here.
    void unregisterStaleServiceWorkers();
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err) => {
      console.warn('[sw] registration failed', err);
    });
  });
}

/**
 * Remove any service worker previously registered on this origin and delete
 * the caches it created, so a stale worker can't keep serving outdated assets
 * during local development. Only our own `egl-*` caches are evicted; unrelated
 * caches are left untouched. Failures are non-fatal — the app still runs.
 */
async function unregisterStaleServiceWorkers(): Promise<void> {
  try {
    if (typeof navigator.serviceWorker.getRegistrations !== 'function') return;
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length === 0) return;
    await Promise.all(registrations.map((registration) => registration.unregister()));
    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(
        keys.filter((key) => key.startsWith('egl-')).map((key) => window.caches.delete(key)),
      );
    }
    console.info('[sw] removed stale service worker(s) on localhost dev');
  } catch (err) {
    console.warn('[sw] failed to remove stale service worker', err);
  }
}
